import cn from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { DynamicPipelineAnalysisDemographicDataLayout } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { DynamicPipelinePhaseResultLayout } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { DemographicTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { Curve } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysisCurve';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { tryTranslateDynamic } from 'libs/end-to-end/src/phrase-utils';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { useDynamicPipeline } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineContext';
import useMeasure from 'react-use-measure';

// eslint-disable-next-line complexity
export const DynamicPipelineAnalysisDemographicBreakdown = ({
  dimensionColumnHeaderValue,
  data,
  selectedLevel,
  nextPhaseResults,
  computedGapBetweenPhases,
  phaseResult,
}: {
  dimensionColumnHeaderValue: string | null;
  data: DynamicPipelineAnalysisDemographicDataLayout;
  selectedLevel: string;
  nextPhaseResults: DynamicPipelinePhaseResultLayout[] | null;
  computedGapBetweenPhases: number;
  phaseResult: DynamicPipelinePhaseResultLayout;
}) => {
  const language = useLanguage();
  const { getLevelByColumnValue, getDimensionByColumnHeaderValue } =
    useDimensionConfig();

  const nextPositiveLevel = useMemo(() => {
    const nextPositiveResult = nextPhaseResults?.[0];
    return nextPositiveResult?.demographic_data.find(
      (dd) => dd.name === data.name,
    );
  }, [data.name, nextPhaseResults]);

  const nextNegativeLevel = useMemo(() => {
    const nextNegativeResult = nextPhaseResults?.[1];
    return nextNegativeResult?.demographic_data.find(
      (dd) => dd.name === data.name,
    );
  }, [data.name, nextPhaseResults]);

  const { setDemographicMeasurement, getDemographicMeasurement } =
    useDynamicPipeline();

  const nextPositiveLevelMeasurement = useMemo(() => {
    return nextPositiveLevel?.uuid
      ? getDemographicMeasurement(nextPositiveLevel.uuid)
      : undefined;
  }, [nextPositiveLevel, getDemographicMeasurement]);

  const nextNegativeLevelMeasurement = useMemo(() => {
    return nextNegativeLevel?.uuid
      ? getDemographicMeasurement(nextNegativeLevel.uuid)
      : undefined;
  }, [nextNegativeLevel, getDemographicMeasurement]);

  const focused = selectedLevel === data.name;
  const otherLevelFocused = !focused && selectedLevel !== '';

  const levelDefinition = useMemo(() => {
    const dimension = dimensionColumnHeaderValue
      ? getDimensionByColumnHeaderValue(dimensionColumnHeaderValue)
      : null;

    return dimension
      ? getLevelByColumnValue(dimension.columnHeaderValue, data.name)
      : null;
  }, [
    data.name,
    dimensionColumnHeaderValue,
    getDimensionByColumnHeaderValue,
    getLevelByColumnValue,
  ]);

  const demographicTitle = levelDefinition?.displayName ?? '';

  const demographicDescription = useMemo(() => {
    try {
      return convertTranslatedContentToReactNode(
        language.piplineAnalysisDemographicDescription({
          phaseName: getTranslatedContentAsString(
            tryTranslateDynamic({
              phrase: phaseResult.name,
              customValue: phaseResult.customName ?? '',
              language,
              logger: uiAppLogger,
            }),
          ),
          count: data.count,
          shortDemographicLabel: demographicTitle.toLocaleLowerCase(),
          pctOfEvent: Number.parseFloat(data.pct_of_event.toFixed(2)),
        }),
      );
    } catch (e) {
      return data.name;
    }
  }, [
    language,
    phaseResult.name,
    phaseResult.customName,
    data.count,
    data.pct_of_event,
    data.name,
    demographicTitle,
  ]);

  const [measureRef, measurements] = useMeasure();

  useEffect(() => {
    if (measurements) {
      setDemographicMeasurement(data.uuid, measurements);
    }
  }, [data.uuid, measurements, setDemographicMeasurement]);

  return (
    <DemographicTooltip
      title={
        <div className="flex flex-col max-w-[200px] gap-1">
          <p className="font-sans text-gray-800 text-base leading-6 font-bold">
            {demographicTitle}
          </p>
          <p className="font-sans text-gray-600 text-xs leading-[18px] font-medium">
            {demographicDescription}
          </p>
        </div>
      }
      placement="right"
    >
      <div
        ref={measureRef}
        className={cn(
          'relative flex flex-col border-solid border-1 rounded justify-start cursor-pointer',
          {
            [`${ConfigColors[levelDefinition?.color ?? 'Gray'].Primary.border}`]:
              focused,
            [`${ConfigColors[levelDefinition?.color ?? 'Gray'].Primary.bg}`]:
              focused,
            [`${ConfigColors[levelDefinition?.color ?? 'Gray'].Secondary.bg}`]:
              !focused,
          },
        )}
        style={{
          height: `${data.height}%`,
        }}
      >
        <div className="flex flex-col py-1 px-1 justify-between align-center">
          <div className="flex flex-row items-center font-sans gap-2 text-[12px] font-semibold justify-between">
            <p
              className={cn('whitespace-nowrap overflow-hidden text-ellipsis', {
                [`${
                  ConfigColors[levelDefinition?.color ?? 'Gray'].Primary
                    .focusedText
                }`]: focused,
                'text-gray-800': !focused,
                'opacity-50': otherLevelFocused,
              })}
            >
              {demographicTitle}
            </p>
            <p
              className={cn('', {
                [`${
                  ConfigColors[levelDefinition?.color ?? 'Gray'].Primary
                    .focusedText
                }`]: focused,
                'text-gray-500': !focused,
                'opacity-50': otherLevelFocused,
              })}
            >
              {data.pct_of_event.toFixed(2)}%
            </p>
          </div>
        </div>
        {nextPositiveLevel &&
          focused &&
          nextPositiveLevelMeasurement &&
          measurements && (
            <Curve
              layoutMeasurements={measurements}
              nextLayoutMeasurements={nextPositiveLevelMeasurement}
              lightened={nextPositiveLevel.pct_of_group > data.pct_of_group}
              color={levelDefinition?.color ?? 'Gray'}
              gap={computedGapBetweenPhases}
            />
          )}
        {nextNegativeLevel &&
          nextPositiveLevel &&
          focused &&
          nextNegativeLevelMeasurement &&
          measurements && (
            <Curve
              layoutMeasurements={measurements}
              nextLayoutMeasurements={nextNegativeLevelMeasurement}
              lightened={nextPositiveLevel.pct_of_group <= data.pct_of_group}
              color={levelDefinition?.color ?? 'Gray'}
              gap={computedGapBetweenPhases}
            />
          )}
      </div>
    </DemographicTooltip>
  );
};
